import { gql } from '@apollo/client';
import {
  UserFragment,
  ProductFragment,
  CategoryFragment,
  CatalogFragment,
  BenefitFragment,
  PaginationFragment,
  CompanyFragment,
  ShopCartFragment,
  PromoCodeFragment,
  OrderBenefitFragment,
  PaymentMethodFragment,
  OrderFragment,
  PlanFragment,
  SettingsFragment,
  ClientFragment,
  ColorsFragment,
  AboutFragment,
  FooterFragment,
  BannerFragment,
  HomeCardsFragment,
} from './fragments';

export const CURRENT_USER = gql`
  query CURRENT_USER($id: String) {
    currentUser(id: $id) {
      user {
        ...UserFragment
      }
    }
  }
  ${UserFragment}
`;

export const GET_USER = gql`
  query GET_USER($filter: FilterFindOneUserInput!) {
    user(filter: $filter) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const GET_USERS = gql`
  query GET_USERS(
    $filter: FilterFindManyUserInput
    $sort: SortFindManyUserInput
  ) {
    users(filter: $filter, sort: $sort) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const GET_TOP_SELLING_PRODUCTS = gql`
  query GetTopSellingProducts($data: GetSalesDashboardInput) {
    getTopSellingProducts(data: $data) {
      topProducts {
        product {
          title
          _id
          category {
            name
          }
          variants {
            price
            _id
            option {
              option1
              option2
              option3
              _id
            }
            compareAtPrice
            photos {
              src
            }
          }
          slug
        }
        amount
      }
    }
  }
`;

export const GET_CATALOGUE = gql`
  query GET_CATALOGUE($data: GetCatalogInput) {
    getCatalog(data: $data) {
      sections {
        category {
          ...CategoryFragment
        }
        products {
          ...ProductFragment
        }
      }
    }
  }
  ${CategoryFragment}
  ${ProductFragment}
`;

export const GET_PRODUCT = gql`
  query GET_PRODUCT(
    $filter: FilterFindOneProductInput
    $skip: Int
    $sort: SortFindOneProductInput
  ) {
    product(filter: $filter, skip: $skip, sort: $sort) {
      ...ProductFragment
    }
  }
  ${ProductFragment}
`;

export const GET_PRODUCT_PAGINATION = gql`
  query GET_PRODUCT_PAGINATION(
    $filter: FilterFindManyProductInput
    $page: Int
    $perPage: Int
    $sort: SortFindManyProductInput
  ) {
    productPagination(
      filter: $filter
      page: $page
      perPage: $perPage
      sort: $sort
    ) {
      count
      items {
        ...CompanyFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${ProductFragment}
`;

export const GET_CATALOG_FILTER_PAGINATION = gql`
  query GET_CATALOG_FILTER_PAGINATION($data: GetCatalogFilterPaginationInput) {
    getCatalogFilterPagination(data: $data) {
      count
      items {
        ...ProductFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${ProductFragment}
  ${PaginationFragment}
`;

export const GET_CATEGORY = gql`
  query GET_CATEGORY(
    $filter: FilterFindOneCategoryInput
    $skip: Int
    $sort: SortFindOneCategoryInput
  ) {
    category(filter: $filter, skip: $skip, sort: $sort) {
      ...CategoryFragment
    }
  }
  ${CategoryFragment}
`;

export const GET_CATEGORY_ALLIES = gql`
  query GET_CATEGORY_ALLIES($data: GetCategoryAlliesInput) {
    getCategoryAllies(data: $data) {
      category {
        ...CategoryFragment
      }
      allies {
        ...CompanyFragment
      }
    }
  }
  ${CategoryFragment}
  ${CompanyFragment}
`;

export const GET_ALL_CATEGORIES_WITH_ALLIES = gql`
  query Allies {
    getAllCategoriesWithAllies {
      allies {
        ...CompanyFragment
      }
      category {
        ...CategoryFragment
      }
    }
  }
  ${CompanyFragment}
  ${CategoryFragment}
`;

export const GET_CATEGORIES = gql`
  query GET_CATEGORIES(
    $filter: FilterFindManyCategoryInput
    $skip: Int
    $limit: Int
    $sort: SortFindManyCategoryInput
  ) {
    categories(filter: $filter, skip: $skip, limit: $limit, sort: $sort) {
      ...CategoryFragment
    }
  }
  ${CategoryFragment}
`;

export const GET_CATALOG = gql`
  query GET_CATALOG($data: GetCatalogInput) {
    getCatalog(data: $data) {
      ...CatalogFragment
    }
  }
  ${CatalogFragment}
`;

export const GET_BENEFITS = gql`
  query GET_BENEFITS(
    $filter: FilterFindManyBenefitInput
    $skip: Int
    $sort: SortFindManyBenefitInput
  ) {
    benefits(filter: $filter, skip: $skip, sort: $sort) {
      ...BenefitFragment
    }
  }
  ${BenefitFragment}
`;

export const GET_CLIENT_BENEFITS = gql`
  query GET_CLIENT_BENEFITS($data: GetClientBenefitsInput) {
    getClientBenefits(data: $data) {
      benefits {
        ...OrderBenefitFragment
      }
    }
  }
  ${OrderBenefitFragment}
`;

export const GET_USER_ADDRESSES = gql`
  query GET_USER_ADDRESSES($data: GetUserAddressesInput) {
    getUserAddresses(data: $data) {
      addresses {
        _id
        address1
        address2
        city
        country
        createdAt
        municipality
        state
        zip
        active
      }
    }
  }
`;

export const GET_PRODUCT_PROFILE = gql`
  query GET_PRODUCT_PROFILE($data: GetProductProfile) {
    getProductProfile(data: $data) {
      product {
        ...ProductFragment
      }
      related {
        ...ProductFragment
      }
      company {
        _id
        name
      }
    }
  }
  ${ProductFragment}
`;

export const GET_SHOP_CART = gql`
  query GET_SHOP_CART(
    $filter: FilterFindOneShopCartInput
    $skip: Int
    $sort: SortFindOneShopCartInput
  ) {
    shopCart(filter: $filter, skip: $skip, sort: $sort) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const GET_SHOP_CART_PAYMENT_STATUS = gql`
  query GetShopCartPaymentStatus($data: GetShopCartTallyInput) {
    getShopCartPaymentStatus(data: $data) {
      paymentStatus
      remainingAmount
    }
  }
`;

export const GET_SHOP_CART_TALLY = gql`
  query GET_SHOP_CART_TALLY($data: GetShopCartTallyInput) {
    getShopCartTally(data: $data) {
      subTotal
      total
      taxBase
      discountTotal
      finalTotal
      taxTotal
    }
  }
`;

export const GET_PROMO_CODE = gql`
  query GET_PROMO_CODE($filter: FilterFindOnePromoCodeInput) {
    promoCode(filter: $filter) {
      ...PromoCodeFragment
    }
  }
  ${PromoCodeFragment}
`;

export const GET_PAYMENT_METHODS = gql`
  query GET_PAYMENT_METHODS(
    $filter: FilterFindManyPaymentMethodInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManyPaymentMethodInput
  ) {
    paymentMethods(filter: $filter, skip: $skip, limit: $limit, sort: $sort) {
      ...PaymentMethodFragment
    }
  }
  ${PaymentMethodFragment}
`;

export const GET_ORDER = gql`
  query GET_ORDER($filter: FilterFindOneOrderInput) {
    order(filter: $filter) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const GET_USER_ORDERS = gql`
  query GET_USER_ORDERS($data: GetUserOrdersInput) {
    getUserOrders(data: $data) {
      orders {
        ...OrderFragment
      }
    }
  }
  ${OrderFragment}
`;

export const COMPANIES = gql`
  query COMPANIES($filter: FilterFindManyCompanyInput) {
    companies(filter: $filter) {
      ...CompanyFragment
    }
  }
  ${CompanyFragment}
`;

export const COMPANY_BRANDING = gql`
  query CompanyBranding($filter: FilterFindOneCompanyBrandingInput) {
    companyBranding(filter: $filter) {
      _id
      createdAt
      updatedAt
      company
      logo {
        image
        link
      }
      colors {
        ...ColorsFragment
      }
      about {
        ...AboutFragment
      }
      banners {
        ...BannerFragment
      }
      homeCards {
        ...HomeCardsFragment
      }
      footer {
        ...FooterFragment
      }
    }
  }
  ${ColorsFragment}
  ${AboutFragment}
  ${BannerFragment}
  ${HomeCardsFragment}
  ${FooterFragment}
`;

export const PLANS = gql`
  query PLANS($filter: FilterFindManyPlanInput) {
    plans(filter: $filter) {
      ...PlanFragment
    }
  }
  ${PlanFragment}
`;

export const GET_SETTINGS = gql`
  query GET_SETTINGS(
    $filter: FilterFindManySettingsInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManySettingsInput
  ) {
    settings(filter: $filter, limit: $limit, skip: $skip, sort: $sort) {
      ...SettingsFragment
    }
  }
  ${SettingsFragment}
`;

export const CLIENT = gql`
  query CLIENT($filter: FilterFindOneClientInput) {
    client(filter: $filter) {
      ...ClientFragment
    }
  }
  ${ClientFragment}
`;

// ################ POINTS ################
export const GET_USER_POINTS = gql`
  query GetUserTotalPoints($data: GetUserOrdersInput) {
    getUserTotalPoints(data: $data) {
      totalPoints
      user {
        ...UserFragment
      }
      benefits {
        ...OrderBenefitFragment
      }
    }
  }
  ${UserFragment}
  ${OrderBenefitFragment}
`;

export const GET_DOLLAR_RATE_IN_BS = gql`
  query BcvDollarRateInBs {
    bcvDollarRateInBs {
      dollarBcv
      success
    }
  }
`;
